import React from 'react';

import { Text } from '@change/ds-core-ui';
import { TikTokIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import type { SocialPostErrorCode } from '../../hooks/useSocialPostUrlForm';

type ErrorVariant = 'error' | 'warning';

// Maps each error code to how it should read: the two `warning` codes are pre-submit
// client blocks, the two `error` codes come back from the server on submit.
const ERROR_VARIANT: Readonly<Record<SocialPostErrorCode, ErrorVariant>> = {
	unsupported_platform: 'warning',
	invalid_link: 'warning',
	video_unavailable: 'error',
	generic_error: 'error',
};

// Container styling when no error is shown (the neutral TikTok helper state).
const DEFAULT_CONTAINER = 'bg-fill-information-weaker border-stroke-info-weak/50';

const variantStyles: Readonly<Record<ErrorVariant, { container: string; text: string }>> = {
	error: { container: 'bg-fill-error-weaker border-stroke-error-weak', text: 'text-typography-error' },
	warning: { container: 'bg-fill-warning-weaker border-stroke-warning-success-weak', text: 'text-typography-warning' },
};

// TranslateHtml: each message leads with a <b>-wrapped sentence.
const ERROR_MESSAGES: Readonly<Record<SocialPostErrorCode, React.ReactNode>> = {
	unsupported_platform: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.unsupported_platform" />,
	invalid_link: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.invalid_link" />,
	video_unavailable: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.video_unavailable" />,
	generic_error: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.generic_error" />,
};

export const FormHelper: React.FC<Props> = ({ className, error }) => {
	const errorVariant = error ? ERROR_VARIANT[error] : undefined;

	return (
		<div
			data-qa="social-post-callout"
			className={cn(
				'rounded-large mx-auto -mt-4 flex max-w-[95%] items-center gap-2 border px-5 pt-6 pb-3',
				errorVariant ? variantStyles[errorVariant].container : DEFAULT_CONTAINER,
				errorVariant && 'items-start',
				className,
			)}
		>
			{error && errorVariant ? (
				<>
					<div
						className={cn(
							'm-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
							errorVariant === 'error' ? 'bg-fill-error-strong' : 'bg-fill-warning-strong',
						)}
					>
						<Text variant="body" size="medium" fontWeight="bold" className="text-typography-inverse-strong">
							!
						</Text>
					</div>
					<Text variant="body" size="small" as="p" className={variantStyles[errorVariant].text}>
						{ERROR_MESSAGES[error]}
					</Text>
				</>
			) : (
				<>
					<div className="bg-fill-strong border-stroke-inverse-strong elevation-low flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2">
						<TikTokIcon className="text-icon-inverse-strong" size="x-small" />
					</div>
					<Text as="p" className="text-typography-weak">
						<Translate value="corgi.homepage.hero_section.input_helper_tiktok" />
					</Text>
				</>
			)}
		</div>
	);
};

type Props = Readonly<{
	className?: string;
	error?: SocialPostErrorCode;
}>;
